import { useEffect, useRef, useState } from "react";
import {
  Layers,
  Factory,
  ShieldCheck,
  Crosshair,
  FileText,
  FlaskConical,
  ArrowRight,
  RotateCw,
  Gauge,
  Clock,
} from "lucide-react";

const STEP_MS = 2600;

/* the six levels of the path, mirroring the app's breadcrumb */
const PATH = [
  {
    icon: Layers,
    title: "SCyWF Job Role Training",
    chip: "FRAMEWORK",
    depth: 0,
    crumbs: ["Learning Paths", "SCyWF Job Role Training"],
    label: "Specialties in SCyWF Job Role Training",
    count: "5 specialties",
    view: "specialties",
  },
  {
    icon: Factory,
    title: "Industrial Control Systems & OT",
    chip: "1 MOD",
    depth: 1,
    crumbs: ["Learning Paths", "SCyWF Job Role Training", "ICS/OT"],
    label: "Inside Industrial Control Systems & OT",
    count: "1 module",
    view: "specialty",
  },
  {
    icon: ShieldCheck,
    title: "ICS/OT Specialty",
    chip: "5 MOD",
    depth: 2,
    crumbs: ["Learning Paths", "…", "ICS/OT Specialty"],
    label: "Job roles in ICS/OT Specialty",
    count: "5 job roles",
    view: "roles",
  },
  {
    icon: Crosshair,
    title: "ICSOT-002 · Infrastructure Specialist",
    chip: "15 MOD",
    depth: 3,
    crumbs: ["Learning Paths", "…", "ICSOT-002: Infrastructure Specialist"],
    label: "Modules in ICSOT-002",
    count: "15 modules",
    view: "modules",
  },
  {
    icon: FileText,
    title: "ICS-SE-03 · Hardening PLCs and RTUs",
    chip: "1 MOD",
    depth: 4,
    crumbs: [
      "Learning Paths",
      "…",
      "ICSOT-002",
      "ICS-SE-03: Hardening PLCs and RTUs",
    ],
    label: "Box in ICS-SE-03",
    count: "1 box",
    view: "box",
  },
  {
    icon: FlaskConical,
    title: "AquaShield Containment",
    chip: "BOX",
    depth: 5,
    crumbs: ["Learning Paths", "…", "ICS-SE-03", "AquaShield Containment"],
    label: "Box in ICS-SE-03",
    count: "1 box",
    view: "box",
  },
];

const SPECIALTIES = [
  {
    name: "Cybersecurity Architecture, Research and Development",
    code: "CARD",
    mods: "2 MODULES",
  },
  {
    name: "Leadership and Workforce Development",
    code: "LWD",
    mods: "2 MODULES",
  },
  {
    name: "Governance, Risk, Compliance and Laws",
    code: "GRCL",
    mods: "2 MODULES",
  },
  { name: "Protection and Defense", code: "PD", mods: "5 MODULES" },
  {
    name: "Industrial Control Systems and Operational Technologies",
    code: "ICS/OT",
    mods: "1 MODULES",
    hot: true,
  },
];

const ROLES = [
  { name: "ICSOT-001: ICS/OT Cybersecurity Architect", mods: "0 MODULES" },
  {
    name: "ICSOT-002: ICS/OT Cybersecurity Infrastructure Specialist",
    mods: "15 MODULES",
    hot: true,
  },
  {
    name: "ICSOT-003: ICS/OT Cybersecurity Defense Analyst",
    mods: "0 MODULES",
  },
  { name: "ICSOT-004: ICS/OT Cybersecurity Risk Officer", mods: "0 MODULES" },
  {
    name: "ICSOT-005: ICS/OT Cybersecurity Incident Responder",
    mods: "0 MODULES",
  },
];

const MODULES = [
  { name: "ICS-SE-01: Designing a Secure OT Architecture", mods: "0 MODULES" },
  { name: "ICS-SE-02: IT/OT Segmentation", mods: "0 MODULES" },
  { name: "ICS-SE-03: Hardening PLCs and RTUs", mods: "1 MODULES", hot: true },
  { name: "ICS-SE-04: Securing SCADA Systems", mods: "1 MODULES" },
  { name: "ICS-SE-05: OT Network Monitoring", mods: "0 MODULES" },
  { name: "ICS-SE-06: OT Incident Response", mods: "0 MODULES" },
];

function useInView(threshold = 0.25) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ob = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          ob.disconnect();
        }
      },
      { threshold },
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, [threshold]);
  return [ref, seen];
}

/* a small live SCADA schematic — the thing that makes an OT card read as OT */
function Schematic({ variant = 0 }) {
  const paths = [
    {
      flow: "M14 20 H70 V44 H130",
      still: "M130 44 V22 H186",
      units: [
        [6, 13],
        [122, 37, true],
        [178, 15],
      ],
    },
    {
      flow: "M14 22 H70 V44 H130 V20 H186",
      still: "",
      units: [
        [6, 15],
        [122, 37, true],
      ],
    },
    {
      flow: "M14 44 H60",
      still: "M60 44 H130 M130 44 H186",
      units: [
        [6, 37],
        [54, 34, true],
        [124, 37],
      ],
    },
  ];
  const p = paths[variant % paths.length];
  return (
    <svg
      viewBox="0 0 200 64"
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 h-full w-full"
      fill="none"
    >
      {p.still && (
        <path
          d={p.still}
          stroke="color-mix(in srgb, var(--color-primary) 40%, transparent)"
          strokeWidth="1.2"
        />
      )}
      <path
        className="lp-ot-flow"
        d={p.flow}
        stroke="var(--color-primary)"
        strokeWidth="1.2"
        strokeDasharray="3 4"
      />
      {p.units.map(([x, y, hot], i) => (
        <rect
          key={i}
          className={hot ? "lp-ot-alarm" : ""}
          x={x}
          y={y}
          width={hot ? 16 : 14}
          height={hot ? 18 : 14}
          rx="2"
          fill="var(--color-bg)"
          stroke={
            hot
              ? "#e2564b"
              : "color-mix(in srgb, var(--color-primary) 50%, transparent)"
          }
          strokeWidth="0.8"
        />
      ))}
      <circle r="1.6" fill="#bff2d4">
        <animateMotion dur="3s" repeatCount="indefinite" path={p.flow} />
      </circle>
    </svg>
  );
}

function Card({ item, index, variant }) {
  return (
    /* square tiles: the thumbnail takes the top half, the title block the
       bottom, so every card is the same shape regardless of name length */
    <div
      className={`lp-ot-pop group relative flex aspect-square flex-col overflow-hidden rounded-xl border bg-(--color-bg-card) transition-all duration-300 hover:-translate-y-0.5 ${
        item.hot
          ? "border-(--color-primary)/60 shadow-[0_0_24px_-10px_var(--color-shadow-primary)]"
          : "border-(--color-border) hover:border-(--color-primary)/50"
      }`}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="relative h-18 shrink-0 overflow-hidden border-b border-(--color-border) bg-(--color-bg)">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-90"
          style={{
            backgroundImage:
              "linear-gradient(color-mix(in srgb, var(--color-primary) 9%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in srgb, var(--color-primary) 9%, transparent) 1px, transparent 1px)",
            backgroundSize: "12px 12px",
          }}
        />
        {item.hot && (
          <>
            <span className="lp-ot-sweep pointer-events-none absolute inset-y-0 w-10" />
            <Schematic variant={variant} />
          </>
        )}
        <span className="absolute right-1.5 top-1.5 z-2 rounded border border-(--color-border-solid) bg-(--color-bg)/85 px-1.5 py-0.5 qv-mono text-[6px] tracking-wider text-(--color-text-secondary)">
          {item.mods}
        </span>
      </div>
      <div className="flex flex-1 flex-col justify-between overflow-hidden p-2.5">
        <div
          className={`text-[10.5px] font-bold leading-snug ${
            item.hot ? "text-(--color-primary)" : "text-(--color-text-primary)"
          }`}
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 4,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {item.name}
        </div>
        {item.code && (
          <div className="mt-1 shrink-0 qv-mono text-[6.5px] tracking-wider text-(--color-text-muted)">
            {item.code}
          </div>
        )}
      </div>
    </div>
  );
}

export default function OtSpotlight() {
  const [ref, seen] = useInView();
  const [step, setStep] = useState(0);
  const [auto, setAuto] = useState(true);

  useEffect(() => {
    if (!seen || !auto) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(
      () => setStep((s) => (s + 1) % PATH.length),
      STEP_MS,
    );
    return () => clearInterval(id);
  }, [seen, auto]);

  const cur = PATH[step];

  const jump = (i) => {
    setAuto(false);
    setStep(i);
  };

  const replay = () => {
    setStep(0);
    setAuto(true);
  };

  return (
    <section id="ics-ot" className="relative py-20 lg:py-28">
      <div className="mx-auto max-w-350 px-5 md:px-10">
        {/* heading */}
        <div className="lp-reveal lp-down mx-auto max-w-2xl text-center">
          <div className="mb-3 flex items-center justify-center gap-2 qv-mono text-[11px] font-medium uppercase tracking-[0.24em] text-(--color-primary)">
            <span className="h-px w-6 bg-(--color-primary) opacity-50" />
            ICS / OT Specialty
            <span className="h-px w-6 bg-(--color-primary) opacity-50" />
          </div>
          <h2 className="qv-glitch mx-auto max-w-2xl text-[34px] sm:text-[42px] font-medium leading-tight tracking-tight text-(--color-text-primary)">
            Follow the path,{" "}
            <span className="lp-highlight">land in a plant</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[14.5px] leading-relaxed text-(--color-text-secondary)">
            ICS/OT runs the full depth of the SCyWF tree — specialty, job role,
            module, box. Watch the route, or click any level to explore it.
          </p>
        </div>

        {/* breadcrumb */}
        <div className="mt-8 flex min-h-5 flex-wrap items-center justify-center gap-2 qv-mono text-[9.5px]">
          {cur.crumbs.map((c, i) => (
            <span key={`${c}-${i}`} className="flex items-center gap-2">
              <span
                className={
                  i === cur.crumbs.length - 1
                    ? "text-(--color-primary)"
                    : "text-(--color-text-muted)"
                }
              >
                {c}
              </span>
              {i < cur.crumbs.length - 1 && (
                <span className="text-(--color-text-muted) opacity-40">›</span>
              )}
            </span>
          ))}
        </div>

        {/* the machine */}
        <div
          ref={ref}
          className="lp-reveal mt-6 overflow-hidden rounded-2xl border border-(--color-border-solid) qv-card qv-noise"
        >
          <div className="flex items-center justify-between border-b border-(--color-border) px-4 py-3 qv-mono text-[9px] uppercase tracking-wider text-(--color-text-muted)">
            <span>Learning path · navigation</span>
            <span className="flex items-center gap-3">
              <button
                onClick={replay}
                className="flex items-center gap-1.5 rounded-full border border-(--color-border-solid) px-2.5 py-1 text-[8.5px] tracking-wider transition-colors duration-200 hover:border-(--color-primary) hover:text-(--color-primary)"
              >
                <RotateCw className="h-2.5 w-2.5" />
                replay
              </button>
              <span className="flex items-center gap-1.5 text-(--color-primary)">
                <span className="cyber-badge-dot" />
                ICS/OT
              </span>
            </span>
          </div>

          <div className="grid lg:grid-cols-[300px_1fr]">
            {/* LEFT · the descending path */}
            <div
              className="border-b border-(--color-border) p-4 lg:border-b-0 lg:border-r"
              style={{
                background:
                  "linear-gradient(180deg, color-mix(in srgb, var(--color-primary) 2%, transparent), transparent)",
              }}
            >
              {PATH.map((n, i) => {
                const Icon = n.icon;
                const lit = i <= step;
                const isCur = i === step;
                return (
                  /* the row owns the indent and the connector rail, so nothing
                     is positioned outside the button and onto its neighbour */
                  <div
                    key={n.title}
                    className="relative mb-1.5"
                    style={{ paddingLeft: `${n.depth * 14}px` }}
                  >
                    {n.depth > 0 && (
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute bottom-1/2 left-0 border-b border-l border-(--color-border-solid)"
                        style={{
                          left: `${(n.depth - 1) * 14 + 7}px`,
                          width: "7px",
                          height: "22px",
                          borderBottomLeftRadius: "6px",
                        }}
                      />
                    )}
                    <button
                      onClick={() => jump(i)}
                      className={`relative flex w-full items-center gap-2.5 overflow-hidden rounded-lg border px-2.5 py-2 text-left transition-all duration-500 ${
                        isCur
                          ? "border-(--color-primary) shadow-[0_0_26px_-10px_var(--color-shadow-primary)]"
                          : lit
                            ? "border-(--color-primary)/40 bg-(--color-bg-card)"
                            : "border-(--color-border) bg-(--color-bg-card) opacity-25"
                      }`}
                    >
                      {isCur && (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-0"
                          style={{
                            background:
                              "linear-gradient(160deg, color-mix(in srgb, var(--color-primary) 12%, transparent), transparent 50%)",
                          }}
                        />
                      )}
                      <span
                        className={`relative grid h-6 w-6 shrink-0 place-items-center rounded-md border transition-all duration-400 ${
                          isCur
                            ? "border-(--color-primary) bg-(--color-primary-100) text-(--color-primary)"
                            : "border-(--color-primary)/35 bg-(--color-primary-50) text-(--color-primary)"
                        }`}
                      >
                        <Icon className="h-2.5 w-2.5" />
                      </span>
                      <span className="relative min-w-0 flex-1 text-[11px] font-semibold leading-snug text-(--color-text-primary)">
                        {n.title}
                      </span>
                      <span
                        className={`relative shrink-0 rounded border px-1.5 py-px qv-mono text-[7px] ${
                          isCur
                            ? "border-(--color-primary)/50 text-(--color-primary)"
                            : "border-(--color-border-solid) text-(--color-text-muted)"
                        }`}
                      >
                        {n.chip}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>

            {/* RIGHT · what the level reveals */}
            <div className="relative p-4">
              <div className="mb-3 flex items-center justify-between qv-mono text-[9px] uppercase tracking-wider text-(--color-text-muted)">
                <span>{cur.label}</span>
                <span>{cur.count}</span>
              </div>
              <div className="relative h-95 overflow-y-auto">
                {/* specialties */}
                {(cur.view === "specialties" || cur.view === "specialty") && (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(168px,168px))] gap-3">
                    {(cur.view === "specialty"
                      ? SPECIALTIES.filter((s) => s.hot)
                      : SPECIALTIES
                    ).map((s, i) => (
                      <Card key={s.name} item={s} index={i} variant={0} />
                    ))}
                  </div>
                )}

                {/* job roles */}
                {cur.view === "roles" && (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(168px,168px))] gap-3">
                    {ROLES.map((r, i) => (
                      <Card key={r.name} item={r} index={i} variant={1} />
                    ))}
                  </div>
                )}

                {/* modules */}
                {cur.view === "modules" && (
                  <div className="grid grid-cols-[repeat(auto-fill,minmax(168px,168px))] gap-3">
                    {MODULES.map((m, i) => (
                      <Card key={m.name} item={m} index={i} variant={2} />
                    ))}
                  </div>
                )}

                {/* the box */}
                {cur.view === "box" && (
                  <div
                    className="lp-ot-pop max-w-sm overflow-hidden rounded-xl border border-(--color-primary)/50"
                    style={{
                      background:
                        "linear-gradient(160deg, color-mix(in srgb, var(--color-primary) 8%, transparent), transparent 46%), var(--color-bg-card)",
                      boxShadow: "0 0 40px -18px var(--color-shadow-primary)",
                    }}
                  >
                    <div className="flex items-center justify-between border-b border-(--color-border) px-3.5 py-3">
                      <h3 className="text-[14.5px] font-bold text-(--color-text-primary)">
                        AquaShield Containment
                      </h3>
                      <span className="rounded border border-(--color-primary)/50 px-1.5 py-0.5 qv-mono text-[7.5px] tracking-wider text-(--color-primary)">
                        BOX
                      </span>
                    </div>
                    <div className="p-3.5">
                      <p className="mb-3 text-[11px] leading-relaxed text-(--color-text-secondary)">
                        The Clearwater Municipal Water Treatment Plant serves
                        50,000 residents and its SCADA system has been running
                        unmonitored for fifteen years. Yesterday, suspicious
                        Modbus…
                      </p>
                      <div className="mb-1.5 qv-mono text-[7.5px] uppercase tracking-wider text-(--color-text-muted)">
                        Learning objectives
                      </div>
                      <div className="flex gap-2 text-[10.5px] leading-relaxed text-(--color-text-secondary)">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-(--color-primary)" />
                        Students will learn to: (1) navigate a three-layer SIEM…
                      </div>
                    </div>
                    <div className="flex items-center gap-3 border-t border-(--color-border) px-3.5 py-3 qv-mono text-[9px] text-(--color-text-muted)">
                      <span className="flex items-center gap-1 text-[#ef9f27]">
                        <Gauge className="h-3 w-3" /> Medium
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" /> 45
                      </span>
                      <span className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-[linear-gradient(180deg,var(--color-primary),var(--color-primary-hover))] px-2.5 py-1.5 text-[10px] font-bold text-(--color-text-on-primary)">
                        GO TO BOX
                        <ArrowRight className="h-2.5 w-2.5" strokeWidth={2.6} />
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <span className="mt-3 block qv-mono text-[8px] tracking-wider text-(--color-text-muted) opacity-70">
                {auto
                  ? "auto-playing · click any level to jump"
                  : "click any level to explore"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
